import Button from "@components/ui/button";
import { cn } from "@lib/cn";
import { DialogBase } from "@modules/slides-library/shared";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Spinner from "@/components/ui/spinner";
import { handleAsyncOperation } from "@/shared";
import {
  useGetSlidesFoldersQuery,
  useUpdateSlidesFolderMutation,
} from "@/shared/services";
import type { TSlidesFolder } from "@/shared/types";
import { SPECIAL_FOLDERS } from "../constants";
import { FoldersSelect } from "../components/folders-select";

const DIALOG_ID = "move-folder-dialog";

const FolderMoveDialog = ({ folder }: { folder: TSlidesFolder }) => {
  "use no memo";
  const dispatch = useAppDispatch();
  const { data: folders } = useGetSlidesFoldersQuery();
  const { mutateAsync: updateFolder, isPending: isLoading } =
    useUpdateSlidesFolderMutation();
  const [selectedFolder, setSelectedFolder] = useState<{
    title: string;
    id: string;
  }>({
    title: "",
    id: "",
  });
  const handleClose = useCallback(() => {
    dispatch(closeDialog(DIALOG_ID));
  }, [dispatch]);

  const handleSubmit = useCallback(async () => {
    if (isLoading) return;

    await handleAsyncOperation({
      operation: async () => {
        await updateFolder({
          id: folder._id,
          parentId: selectedFolder.id,
        });
      },
      onError: () => toast.error("Failed to move folder"),
      onFinally: handleClose,
    });
  }, [updateFolder, folder._id, selectedFolder.id, handleClose, isLoading]);

  useEffect(() => {
    if (!folders) return;

    if (SPECIAL_FOLDERS[folder.parentId ?? ""]) {
      setSelectedFolder({
        title: SPECIAL_FOLDERS[folder.parentId ?? ""],
        id: folder.parentId ?? "",
      });
      return;
    }

    const currentFolder = folders.find(
      (f: TSlidesFolder) => f._id === folder.parentId,
    );
    if (!currentFolder) return;
    setSelectedFolder({ title: currentFolder.title, id: currentFolder._id });
  }, [folders, folder.parentId]);

  return (
    <DialogBase>
      <DialogBase.Header
        title={`Move “${folder.title}”`}
        onClose={handleClose}
      />
      <DialogBase.Content>
        <FoldersSelect
          folder={selectedFolder}
          setFolder={setSelectedFolder}
          disabledFolderId={folder._id}
        />
      </DialogBase.Content>
      <DialogBase.Footer>
        <Button variant="secondary" className="tw-mr-4" onClick={handleClose}>
          Go back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!selectedFolder.id}
          className={cn(isLoading && "tw-cursor-not-allowed")}
        >
          {isLoading ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Saving...
            </>
          ) : (
            "Save changes"
          )}
        </Button>
      </DialogBase.Footer>
    </DialogBase>
  );
};

FolderMoveDialog.id = DIALOG_ID;
export { FolderMoveDialog };
